import React, { useState, useEffect, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { H1, Text } from '@jarvis.oddle/core/components/Typo'
import { init } from '@jarvis.oddle/core/tools/lib'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import IconMessenger from 'src/components/icons/IconMessenger'
import IconWhatsApp from 'src/components/icons/IconWhatsApp'
import IconTelegram from 'src/components/icons/IconTelegram'
import IconLineApp from 'src/components/icons/IconLineApp'
import IconWeChatApp from 'src/components/icons/IconWeChatApp'
import Flex from '@jarvis.oddle/core/components/Flex'
import showNotificationToast from '@jarvis.oddle/core/components/ToastNotification'
import IconDuplicate from 'src/components/icons/IconDuplicate'
import useTracker from 'src/hooks/useTracker'

const SocialMediaSharingContainer = styled.div`
  margin: 30px 0;
`

const StyledCopyLinkButton = styled.div<{ buttonColor: string }>`
  width: 52px;
  height: 52px;
  background-color: ${(props) => props.buttonColor};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledDivider = styled.div`
  margin-top: 20px;
`

const StyledTextWeChat = styled(Text)`
  margin-top: 9px;
`

const StyledTextCopyLink = styled(Text)`
  margin-top: 7px;
`

interface SocialMediaSharingProps {
  text: string
  widgetId: string
  publicUrl: string
  storeOrder: Oms.V4.StoreOrder
}

const SocialMediaSharing = ({
  text,
  widgetId,
  publicUrl,
  storeOrder
}: SocialMediaSharingProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const theme = useContext(ThemeContext)
  const [isReady, setIsReady] = useState(false)
  const { segmentTrackerOrderedShare } = useTracker()

  useEffect(() => {
    ;(async () => {
      await init(
        `//s7.addthis.com/js/300/addthis_widget.js#pubid=${widgetId}`,
        `addthis-${widgetId}`
      )
      if (window.addthis) {
        window.addthis.update('share', 'url', publicUrl)
        window.addthis.toolbox('.addthis_toolbox')
        setIsReady(true)
      }
    })()
  }, [widgetId, publicUrl])

  const copyPublicUrlToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl)
      showNotificationToast.success(t('Link has been copied to clipboard'))
    } catch (e) {
      // Do nothing
      console.log('Error copy public url to clipboard', e)
    }
  }

  return (
    <SocialMediaSharingContainer>
      {isReady && <H1 textAlign='center'>{text || t('Share')}</H1>}
      <div className='addthis_toolbox'>
        <Flex justifyContent='space-around'>
          <a className='addthis_button_whatsapp'>
            <Flex
              justifyContent='center'
              flexDirection='column'
              alignItems='center'
              onClick={() =>
                segmentTrackerOrderedShare({
                  ...storeOrder,
                  appName: 'Whatsapp'
                })
              }
            >
              <IconWhatsApp width={64} height={64} />
              <Text>{t('Whatsapp')}</Text>
            </Flex>
          </a>
          <a className='addthis_button_messenger'>
            <Flex
              justifyContent='center'
              flexDirection='column'
              alignItems='center'
              onClick={() =>
                segmentTrackerOrderedShare({
                  ...storeOrder,
                  appName: 'Messenger'
                })
              }
            >
              <IconMessenger width={64} height={64} />
              <Text>{t('Messenger')}</Text>
            </Flex>
          </a>
          <a className='addthis_button_telegram'>
            <Flex
              justifyContent='center'
              flexDirection='column'
              alignItems='center'
              onClick={() =>
                segmentTrackerOrderedShare({
                  ...storeOrder,
                  appName: 'Telegram'
                })
              }
            >
              <IconTelegram width={64} height={64} />
              <Text>{t('Telegram')}</Text>
            </Flex>
          </a>
        </Flex>
        <StyledDivider />
        <Flex justifyContent='space-around'>
          <a className='addthis_button_lineme'>
            <Flex
              justifyContent='center'
              flexDirection='column'
              alignItems='center'
              onClick={() =>
                segmentTrackerOrderedShare({ ...storeOrder, appName: 'Line' })
              }
            >
              <IconLineApp width={64} height={64} />
              <Text>{t('Line')}</Text>
            </Flex>
          </a>
          <a className='addthis_button_wechat'>
            <Flex
              justifyContent='center'
              flexDirection='column'
              alignItems='center'
              onClick={() =>
                segmentTrackerOrderedShare({ ...storeOrder, appName: 'WeChat' })
              }
            >
              <IconWeChatApp width={54} height={54} />
              <StyledTextWeChat>{t('WeChat')}</StyledTextWeChat>
            </Flex>
          </a>
          <Flex
            justifyContent='center'
            flexDirection='column'
            alignItems='center'
          >
            <StyledCopyLinkButton
              buttonColor={theme.buttonColor}
              onClick={copyPublicUrlToClipboard}
            >
              <IconDuplicate
                height={25}
                width={25}
                fill={theme.buttonTextColor}
              />
            </StyledCopyLinkButton>
            <StyledTextCopyLink>{t('Copy Link')}</StyledTextCopyLink>
          </Flex>
        </Flex>
      </div>
    </SocialMediaSharingContainer>
  )
}

export default SocialMediaSharing
